import React, { useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd } from "../../constants/styles";
import { gradCardStyle, gradLift, glassPill, glassTile } from "../../constants/gradCard";
import MC from "../common/MC";
import { formatPropAddr } from "../../utils/propertyAddr";

function SpDash({ sp }) {
  const { t, h, go, setSelMaintId, sps, currUserId, flash } = useContext(Ctx);
  if (sp.loading) return <div style={{ paddingTop: 40, textAlign: "center" }}><div style={{ color: h.sc, fontSize: 13 }}>{t.syncingDb}</div></div>;
  const spJobs = sp.data?.jobs || []; const spPayments = sp.data?.payments || []; const spProfile = sp.data?.profile;
  const spTens = sp.data?.tens || []; const spProps = sp.data?.props || [];

  const pending = spJobs.filter(j => j.sp_status === 'pending' || j.sp_status === 'accepted').length;
  const inProg = spJobs.filter(j => j.sp_status === 'in_progress').length;
  const completed = spJobs.filter(j => j.sp_status === 'completed' || j.confirmed_by_tenant).length;
  // Mirror SpEarnings' merge: service_payments rows are authoritative, and
  // completed jobs whose charges aren't settled into service_payments yet
  // still count. The dash previously read only service_payments, showing
  // "Pending Pay ₹0" while the Earnings page showed the submitted charges.
  const completedJobsList = spJobs.filter(j => j.sp_status === 'completed' || j.confirmed_by_tenant);
  const paidMaintIds = new Set(spPayments.map(p => p.maint_id).filter(Boolean));
  const earnEntries = [
    ...spPayments.map(p => ({ amount: p.amount || 0, st: p.st })),
    ...completedJobsList.filter(j => (j.sp_pay_amt || 0) > 0 && !paidMaintIds.has(j.id))
      .map(j => ({ amount: j.sp_pay_amt || 0, st: j.sp_pay_st === 'paid' ? 'paid' : 'pending' })),
  ];
  const totalEarned = earnEntries.filter(e => e.st === 'paid').reduce((s, e) => s + e.amount, 0);
  const pendingPay = earnEntries.filter(e => e.st !== 'paid').reduce((s, e) => s + e.amount, 0);
  const recentJobs = spJobs.filter(j => j.sp_status !== 'completed' && !j.confirmed_by_tenant).slice(0, 3);

  // Same status-update helper as SpJobsList. Optimistic flash + reload
  // so the dashboard stays in sync after Accept / Start Work / Complete.
  const updateStatus = async (job, newStatus, e) => {
    e?.stopPropagation?.();
    const updates = { id: job.id, sp_status: newStatus };
    if (newStatus === 'completed') {
      updates.completion_token = `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    await api.serviceUpdateJob(updates);
    flash(newStatus === 'accepted' ? t.jobAccepted : newStatus === 'in_progress' ? t.workStarted : t.jobCompleted);
    sp.reload();
  };

  // Check location from both sync data AND sps context (updated immediately after profile save)
  const spFromCtx = (sps || []).find(s => s.id === currUserId);
  const hasLocation = spProfile?.latitude || spProfile?.lat || spFromCtx?.latitude || spFromCtx?.lat;

  return <div style={{ paddingTop: 16 }}>
    <h2 style={{ color: h.tx, fontSize: 20, fontWeight: 700, margin: "0 0 4px" }}>{t.welcome}{spProfile?.name ? `, ${spProfile.name}` : ""}!</h2>
    <p style={{ color: h.sc, fontSize: 12, margin: "0 0 16px" }}>{spProfile?.skill || ""}</p>

    {/* Location prompt if not set */}
    {!hasLocation && <div onClick={() => go("profile")} style={{ display: "flex", alignItems: "center", gap: 10, background: `${h.wn}10`, border: `1px solid ${h.wn}25`, borderRadius: 12, padding: "12px 14px", marginBottom: 16, cursor: "pointer" }}>
      <span style={{ fontSize: 20 }}>{"\uD83D\uDCCD"}</span>
      <div style={{ flex: 1 }}>
        <div style={{ color: h.wn, fontWeight: 600, fontSize: 12 }}>Pin your shop location</div>
        <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>Required to receive nearby job assignments</div>
      </div>
      <span style={{ color: h.wn, fontSize: 14, fontWeight: 700 }}>{"\u2192"}</span>
    </div>}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
      <MC ic="wrench" l={t.totalAssigned} v={spJobs.length} cl={h.ac} grad={["#60a5fa", "#4f46e5"]} h={h} onClick={() => go("spJobs")} />
      <MC ic="zap" l={t.pendingJobs} v={pending} cl={h.wn} grad={pending > 0 ? ["#fbbf24", "#d97706"] : ["#34d399", "#059669"]} h={h} onClick={() => go("spJobs")} />
      <MC ic="gear" l={t.inProgressJobs} v={inProg} cl={h.pp} grad={["#a78bfa", "#7c3aed"]} h={h} onClick={() => go("spJobs")} />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
      <MC ic="shield" l={t.completedJobs} v={completed} cl={h.gn} grad={["#34d399", "#059669"]} h={h} onClick={() => go("spHistory")} />
      <MC ic="dollar" l={t.totalEarnings} v={`₹${totalEarned.toLocaleString()}`} cl={h.gn} grad={["#34d399", "#059669"]} h={h} onClick={() => go("spEarnings")} />
      <MC ic="dollar" l={t.pendingPay} v={`₹${pendingPay.toLocaleString()}`} cl={h.wn} grad={pendingPay > 0 ? ["#fbbf24", "#d97706"] : ["#34d399", "#059669"]} h={h} onClick={() => go("spEarnings")} />
    </div>
    {recentJobs.length > 0 && <><div style={{ color: h.tx, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>{t.recentJobs}</div>
    {recentJobs.map(j => {
      const ten = spTens.find(x => x.id === j.tid);
      // Same property fallback as SpJobsList — when there's exactly one
      // property in the sync result and no tenant resolved, use it.
      const prop = ten ? spProps.find(x => x.id === ten.pid) : (spProps.length === 1 ? spProps[0] : null);
      const mapsUrl = prop?.latitude && prop?.longitude
        ? `https://www.google.com/maps/dir/?api=1&destination=${prop.latitude},${prop.longitude}`
        : null;
      const tone = j.sp_status === 'pending' ? "amber" : j.sp_status === 'in_progress' ? "violet" : "indigo";
      return (
        <div key={j.id}
          onMouseOver={(e) => gradLift(e, tone, true)}
          onMouseOut={(e) => gradLift(e, tone, false)}
          style={{ ...crd(h), ...gradCardStyle(tone) }}>
          {/* Header — title, tenant·property, date + urgency/status pills */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <div style={{ color: "#fff", fontWeight: 600, fontSize: 13 }}>{j.desc}</div>
              <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, marginTop: 2 }}>
                {ten?.name || (t.generalJob || "General Job")} · {prop ? formatPropAddr(prop) : (t.noProperty || "No property")}
              </div>
              {j.dt && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 10 }}>{j.dt}</div>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
              <span style={glassPill}>
                {j.urg === "high" ? t.high : j.urg === "med" ? t.med : t.low}
              </span>
              <span style={glassPill}>
                {j.sp_status === 'pending' ? t.pending
                  : j.sp_status === 'accepted' ? (t.accepted || "Accepted")
                  : j.sp_status === 'in_progress' ? (t.inProgress || "In Progress")
                  : j.sp_status}
              </span>
            </div>
          </div>

          {/* Get Directions */}
          {mapsUrl && (
            <a href={mapsUrl} target="_blank" rel="noreferrer" style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              width: "100%", padding: "8px 0", borderRadius: 10,
              background: "rgba(255,255,255,0.92)", border: "1px solid rgba(255,255,255,0.6)",
              color: "#0f9d6b", fontSize: 12, fontWeight: 600,
              textDecoration: "none", marginBottom: 8,
            }}>🗺️ Get Directions</a>
          )}

          {/* Phone link */}
          {ten?.ph && (
            <div style={{ marginBottom: 8 }}>
              <a href={`tel:${ten.ph}`} style={{ color: "#fff", fontSize: 12, fontWeight: 600 }}>{"📞 " + ten.ph}</a>
            </div>
          )}

          {/* Action buttons — same state machine as SpJobsList */}
          <div style={{ display: "flex", gap: 8 }}>
            {j.sp_status === 'pending' && (
              <button onClick={(e) => updateStatus(j, 'accepted', e)} style={glassTile(h.ac)}>
                {t.acceptJob}
              </button>
            )}
            {j.sp_status === 'accepted' && (
              <button onClick={(e) => updateStatus(j, 'in_progress', e)} style={glassTile(h.pp || h.ac)}>
                {t.startWork}
              </button>
            )}
            {(j.sp_status === 'in_progress' || j.sp_status === 'completed') && (
              <>
                <button onClick={(e) => { e.stopPropagation(); setSelMaintId(j.id); go("spJobDetail"); }} style={glassTile(h.ac)}>
                  {(j.sp_pay_amt || 0) > 0 ? (t.editCharges || "Edit Charges") : t.jobDetails}
                </button>
                {j.sp_status === 'in_progress' && (
                  <button onClick={(e) => updateStatus(j, 'completed', e)} style={glassTile(h.gn)}>
                    {t.markComplete}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      );
    })}</>}
  </div>;
}

export default SpDash;
